import { ActionIcon, Checkbox, Flex } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import dayjs from "dayjs";
import { type ChangeEvent, memo, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "src/redux/hooks";
import { setEditing } from "src/redux/slices/entrySlice";

import { useInputProps } from "../hooks/useInputProps";
import TimeInputWithSuffix from "../TimeInputWithSuffix/TimeInputWithSuffix";
import { type SteeringPumpRowValue } from "./SteeringPumps.types";

interface Props {
  id: string;
  index: number;
  length: number;
  value: SteeringPumpRowValue;
  remove: (index: number) => void;
  disabled?: boolean;
}

const SteeringPumpRow = ({
  id,
  index,
  length,
  value,
  remove,
  disabled,
}: Props) => {
  const dispatch = useDispatch();
  const { setValue } = useFormContext();

  const {
    value: checked,
    onChange: onCheckChange,
    disabled: disabledByInput,
  } = useInputProps(`${id}.${index}.check`, value.check);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onCheckChange(event.target.checked);
    if (event.target.checked) {
      setValue(`${id}.${index}.time`, dayjs().format("HH:mm"));
      for (let i = 0; i < length; i++) {
        if (i !== index) {
          setValue(`${id}.${i}.check`, false);
        }
      }
    }
    dispatch(setEditing(true));
  };

  const handleRemove = useCallback(() => {
    remove(index);
    dispatch(setEditing(true));
  }, [dispatch, index, remove]);

  return (
    <Flex align="center" gap="md" miw={350}>
      <Checkbox
        miw={100}
        size="md"
        labelPosition="left"
        name={value.label}
        label={value.label}
        checked={checked}
        onChange={handleChange}
        disabled={disabled ?? disabledByInput}
      />
      <TimeInputWithSuffix
        id={`${id}.${index}.time`}
        value={value?.time}
        disabled={!checked || disabled}
      />
      {index > 1 && (
        <ActionIcon color="red" onClick={handleRemove}>
          <IconTrash />
        </ActionIcon>
      )}
    </Flex>
  );
};

export default memo(SteeringPumpRow);
